import React, { useEffect, useState } from 'react'

import iconSearch from '../../../../../../Assets/Icon/AdminKycQueue/mna0yq0f-rbvml9l.svg'
import iconFilter from '../../../../../../Assets/Icon/AdminInvestors/mna1u69m-8naty3z.svg'
import iconExport from '../../../../../../Assets/Icon/AdminInvestors/mna1u69m-3g3xrs3.svg'
import iconPrev from '../../../../../../Assets/Images/AdminInvestors/mna1u63v-h50m4oa.png'
import iconNext from '../../../../../../Assets/Icon/AdminInvestors/mna1u63v-bt5ccwl.svg'

import investorTokenAssetsService, {
  type InvestorTokenAsset,
  type InvestorTokenAssetStatus,
  type SortOrder,
} from 'Services/investorTokenAssetsService'
import { ModalWrapper } from 'Components'
import { TimeConverter } from 'Helpers/TimeConverter'

const formatNairaMajor = (value: number | undefined | null) => {
  if (value === undefined || value === null) return '-'
  return `₦${value.toLocaleString()}`
}

type Props = {
  accountId: string
}

const InvestmentsList: React.FC<Props> = ({ accountId }) => {
  const [investmentsLoading, setInvestmentsLoading] = useState(false)
  const [investmentsError, setInvestmentsError] = useState<string | null>(null)
  const [investments, setInvestments] = useState<InvestorTokenAsset[]>([])
  const [investmentPreview, setInvestmentPreview] = useState<InvestorTokenAsset | null>(null)

  const [investmentStatus, setInvestmentStatus] = useState<InvestorTokenAssetStatus>('ACTIVE')
  const [investmentIdInput, setInvestmentIdInput] = useState('')
  const [investmentIdFilter, setInvestmentIdFilter] = useState<string | undefined>(undefined)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [sortOrder] = useState<SortOrder>('ASC')
  const [paginationData, setPaginationData] = useState<{
    pageNumber: number
    pageSize: number
    total: number
  }>({
    pageNumber: 1,
    pageSize: 10,
    total: 0,
  })

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  useEffect(() => {
    const fetchInvestments = async () => {
      setInvestmentsLoading(true)
      setInvestmentsError(null)
      try {
        const response = await investorTokenAssetsService.list({
          pageNumber,
          pageSize,
          sortOrder,
          status: investmentStatus,
          accountId,
          id: investmentIdFilter,
        })
        const data = response?.data?.data
        setInvestments((data?.dataset ?? []) as InvestorTokenAsset[])
        setPaginationData({
          pageNumber: Number(data?.pageNumber ?? pageNumber),
          pageSize: Number(data?.pageSize ?? pageSize),
          total: Number(data?.total ?? 0),
        })
      } catch (e) {
        setInvestments([])
        setPaginationData({ pageNumber, pageSize, total: 0 })
        setInvestmentsError(getErrorMessage(e) ?? 'Failed to load investments')
      } finally {
        setInvestmentsLoading(false)
      }
    }

    fetchInvestments()
  }, [accountId, investmentIdFilter, investmentStatus, pageNumber, pageSize, sortOrder])

  const totalPages = Math.max(1, Math.ceil(paginationData.total / paginationData.pageSize))

  return (
    <>
      <div className='admin-investor__toolbar'>
        <form
          className='admin-investor__search-form'
          onSubmit={(e) => {
            e.preventDefault()
            setPageNumber(1)
            const nextId = investmentIdInput.trim()
            setInvestmentIdFilter(nextId ? nextId : undefined)
          }}
        >
          <div className='admin-investor-search'>
            <img src={iconSearch} alt='' className='admin-investor-search__icon' />
            <input
              value={investmentIdInput}
              onChange={(e) => setInvestmentIdInput(e.target.value)}
              className='admin-investor-search__input'
              placeholder='Search by Investment ID'
            />
          </div>
          <button type='submit' className='admin-investor-button'>
            <img src={iconFilter} alt='' className='admin-investor-button__icon' />
            <span>Search</span>
          </button>
        </form>

        <button
          type='button'
          className='admin-investor-button'
          onClick={() => {
            setPageNumber(1)
            setInvestmentStatus((s) =>
              s === 'ACTIVE' ? 'CANCELLED' : s === 'CANCELLED' ? 'ALL' : 'ACTIVE',
            )
          }}
        >
          <img src={iconFilter} alt='' className='admin-investor-button__icon' />
          <span>Status: {investmentStatus}</span>
        </button>

        <button type='button' className='admin-investor-button'>
          <img src={iconExport} alt='' className='admin-investor-button__icon' />
          <span>Export</span>
        </button>
      </div>

      <div className='admin-investor-table'>
        <div className='admin-investor-table__head'>
          <div>Investment ID</div>
          <div>Principal</div>
          <div>ROI (%)</div>
          <div>Purchase Date</div>
          <div>Status</div>
          <div className='text-end'>Action</div>
        </div>
        <div className='admin-investor-table__body'>
          {investmentsLoading && (
            <div className='admin-investor-table__row'>
              <div className='admin-investor-table__cell'>Loading...</div>
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
            </div>
          )}

          {!investmentsLoading && investmentsError && (
            <div className='admin-investor-table__row'>
              <div className='admin-investor-table__cell'>{investmentsError}</div>
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
            </div>
          )}

          {!investmentsLoading && !investmentsError && investments.length === 0 && (
            <div className='admin-investor-table__row'>
              <div className='admin-investor-table__cell'>No investments found</div>
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
              <div className='admin-investor-table__cell' />
            </div>
          )}

          {!investmentsLoading &&
            !investmentsError &&
            investments.map((r) => (
              <div key={r.id} className='admin-investor-table__row'>
                <div className='admin-investor-table__cell'>{r.id.substring(0, 15)}...</div>
                <div className='admin-investor-table__cell'>
                  {formatNairaMajor(r.principalNairaMajor)}
                </div>
                <div className='admin-investor-table__cell'>
                  {r.percentageReturnMonthly != null ? `${r.percentageReturnMonthly}%` : '-'}
                </div>
                <div className='admin-investor-table__cell'>{TimeConverter(r.purchaseDate)}</div>
                <div className='admin-investor-table__cell'>
                  <span
                    className={[
                      'admin-investor-table__status',
                      r.status === 'ACTIVE'
                        ? 'admin-investor-table__status--active'
                        : r.status === 'CLOSED'
                          ? 'admin-investor-table__status--closed'
                          : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {r.status ?? '-'}
                  </span>
                </div>
                <div className='admin-investor-table__cell text-end'>
                  <button
                    type='button'
                    className='admin-investor-table__action'
                    onClick={() => setInvestmentPreview(r)}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
        </div>

        <div className='admin-investors-pagination'>
          <button
            type='button'
            className='admin-investors-pagination__button'
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1 || investmentsLoading}
          >
            <img src={iconPrev} alt='' className='admin-investors-pagination__icon' />
            <span>Previous</span>
          </button>
          <div className='admin-investors-pagination__numbers'>
            Page {pageNumber} of {totalPages}
          </div>
          <button
            type='button'
            className='admin-investors-pagination__button'
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
            disabled={pageNumber >= totalPages || investmentsLoading}
          >
            <span>Next</span>
            <img src={iconNext} alt='' className='admin-investors-pagination__icon' />
          </button>
        </div>
      </div>

      <ModalWrapper
        showModal={Boolean(investmentPreview)}
        closeModal={() => setInvestmentPreview(null)}
        title='Investment Details'
        size='lg'
      >
        {investmentPreview ? (
          <div>
            <div className='row g-3'>
              <div className='col-12 col-md-6'>
                <div className='admin-kyc-info'>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Investment ID</div>
                    <div className='admin-kyc-info__value'>{investmentPreview.id}</div>
                  </div>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Status</div>
                    <div className='admin-kyc-info__value'>{investmentPreview.status ?? '-'}</div>
                  </div>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Purchase Date</div>
                    <div className='admin-kyc-info__value'>
                      {TimeConverter(investmentPreview.purchaseDate)}
                    </div>
                  </div>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Tokens Purchased</div>
                    <div className='admin-kyc-info__value'>
                      {investmentPreview.tokensPurchased ?? '-'}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6'>
                <div className='admin-kyc-info'>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Principal</div>
                    <div className='admin-kyc-info__value'>
                      {formatNairaMajor(investmentPreview.principalNairaMajor)}
                    </div>
                  </div>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Monthly ROI</div>
                    <div className='admin-kyc-info__value'>
                      {investmentPreview.percentageReturnMonthly != null
                        ? `${investmentPreview.percentageReturnMonthly}%`
                        : '-'}
                    </div>
                  </div>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Term (months)</div>
                    <div className='admin-kyc-info__value'>
                      {investmentPreview.termMonths ?? '-'}
                    </div>
                  </div>
                  <div className='admin-kyc-info__row'>
                    <div className='admin-kyc-info__label'>Next Payout Due</div>
                    <div className='admin-kyc-info__value'>
                      {TimeConverter(investmentPreview.nextPayoutDueAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='mt-3'>
              <div className='admin-kyc-card__title'>Blockchain Info</div>
              <div className='admin-kyc-info'>
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Tx Hash</div>
                  <div className='admin-kyc-info__value'>
                    {investmentPreview.blockchainInfo?.blockchainTxHash ?? '-'}
                  </div>
                </div>
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Block Number</div>
                  <div className='admin-kyc-info__value'>
                    {investmentPreview.blockchainInfo?.blockchainBlockNumber ?? '-'}
                  </div>
                </div>
                <div className='admin-kyc-info__row'>
                  <div className='admin-kyc-info__label'>Registered At</div>
                  <div className='admin-kyc-info__value'>
                    {TimeConverter(investmentPreview.blockchainInfo?.blockchainRegisteredAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </ModalWrapper>
    </>
  )
}

export default InvestmentsList
